// components/ArtworkNavigation.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useArtworkNavigation } from '../../hooks/useArtworkNavigation';
import { ArtworkStub } from '../../models/Artwork';
import './ArtNavigationStyleSheet.css';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
interface ArtworkNavigationProps {
    currentArtworkId: number;
    artworks: ArtworkStub[];
    iconVariant?: 'standard' | 'thin' | 'thick' | 'rounded' | 'sharp';
}

const ArtworkNavigation: React.FC<ArtworkNavigationProps> = ({
    currentArtworkId,
    artworks,
    iconVariant
}) => {
    const navigate = useNavigate();

    const {
        currentIndex,
        hasPrevious,
        hasNext,
        totalCount,
        navigateRelative,
        handleKeyDown
    } = useArtworkNavigation({
        artworks,
        currentArtworkId,
    });

    const getIcons = () => {
        switch (iconVariant) {
            case 'thin':
                return {
                    prev: <ArrowBackIosNewIcon fontSize="medium" />,
                    next: <ArrowForwardIosIcon fontSize="medium" />,
                    back: <ArrowBackIcon fontSize="small" />
                };
            case 'thick':
                return {
                    prev: <NavigateBeforeIcon fontSize="medium" />,
                    next: <NavigateNextIcon fontSize="medium" />,
                    back: <HomeIcon fontSize="small" />
                };
            case 'rounded':
                return {
                    prev: <ChevronLeftIcon fontSize="medium" />,
                    next: <ChevronRightIcon fontSize="medium" />,
                    back: <CloseIcon fontSize="small" />
                };
            case 'sharp':
                return {
                    prev: <KeyboardArrowLeftIcon fontSize="medium" />,
                    next: <KeyboardArrowRightIcon fontSize="medium" />,
                    back: <ArrowBackIcon fontSize="small" />
                };
            default: // standard
                return {
                    prev: <ArrowBackIosNewIcon fontSize="small" />,
                    next: <ArrowForwardIosIcon fontSize="small" />,
                    back: <HomeIcon fontSize="small" />
                };
        }
    };

    const icons = getIcons();

    // Swipe gestures for mobile
    const [touchStart, setTouchStart] = React.useState(0);

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStart - touchEnd;

        if (Math.abs(diff) > 50) {
            if (diff > 0 && hasNext) {
                navigateRelative('next');
            } else if (diff < 0 && hasPrevious) {
                navigateRelative('prev');
            }
        }
    };

    if (artworks.length <= 1) return null;

    return (
        <>
            {/* Main navigation bar */}
            <div className="artwork-navigation">
                <div className="navigation-content">
                    <div className="navigation-counter">
                        <span className="counter-text">
                            {currentIndex + 1} / {totalCount}
                        </span>
                    </div>

                    <div className="navigation-buttons">
                        <button
                            className="nav-button prev-button"
                            onClick={() => navigateRelative('prev')}
                            disabled={!hasPrevious}
                            aria-label="Previous artwork"
                        >
                            <span className="button-icon">
                                {icons.prev}
                            </span>
                            <span className="button-text">
                                {hasPrevious && artworks[currentIndex - 1].artWorkTitle}
                            </span>
                        </button>

                        <button
                            className="nav-button back-button"
                            onClick={() => navigate('/gallery')}
                            aria-label="Back to gallery"
                        >
                            <span className="button-icon">
                                {icons.back}
                            </span>
                            <span className="button-text">
                                Back to Gallery
                            </span>
                        </button>

                        <button
                            className="nav-button next-button"
                            onClick={() => navigateRelative('next')}
                            disabled={!hasNext}
                            aria-label="Next artwork"
                        >
                            <span className="button-text">
                                {hasNext && artworks[currentIndex + 1].artWorkTitle}
                            </span>
                            <span className="button-icon">
                                {icons.next}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Floating overlay buttons */}
            <div className="floating-navigation-overlay">
                <button
                    className="floating-nav-button floating-prev"
                    onClick={() => navigateRelative('prev')}
                    disabled={!hasPrevious}
                    aria-label="Previous artwork"
                >
                    <ArrowBackIosNewIcon className="floating-icon" />
                </button>

                <button
                    className="floating-nav-button floating-next"
                    onClick={() => navigateRelative('next')}
                    disabled={!hasNext}
                    aria-label="Next artwork"
                >
                    <ArrowForwardIosIcon className="floating-icon" />
                </button>
            </div>
        </>
    );
};

export default ArtworkNavigation;